import React, { useMemo, useState } from 'react';
import { connect } from 'react-redux';

// Actions
import { saveContactRiskProfile, SET_IS_SAVING_CONTACT_RISK_PROFILE } from '@/redux/modules/contacts';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';

// Components
import DataTableV2 from '../data/DataTableV2/DataTableV2';
import EmptyStateCard from '@/views/components/misc/EmptyStateCard';
import FormattedMessageV2 from '@/views/components/i18n/FormattedMessageV2';
import ModalV2 from '@/views/components/modals/ModalV2';
import RiskProfileCard from '../common/cards/RiskProfileCard';
import { NameRiskProfile } from '@/views/components/modals/risk-profile/components/NameRiskProfile';
import { PillLabel } from '@/views/components/misc';
import { PrimaryButton } from '../buttons';
import { Tooltip } from '@/views/components/tooltips';
import { withErrorBoundary } from '@/views/components/errors';

// Enums
import { RISK_PROFILES_TABLE_KEYS } from '@/enums/RiskProfiles';

// Libraries
import moment from 'moment';
import { ColumnDef, Row } from '@tanstack/react-table';

// Types
import ContactRiskProfile from '@/types/contact-risk-profile';
import GenericContact from '@/types/generic-contact';
import KycQuestionnaire from '@/types/kyc-questionnaire';
import Locale from '@/types/locale';
import LocalizedString from '@/types/localized-string';
import { ReduxState } from '@/redux/modules/types';
import { TableClassNames } from '@/types/data-table';

// Utils
import notification from '@/utils/notification';
import { customSorting } from '@/utils/data-table';
import { formatLocalizedString, formatMessage, FormattedMessage } from '@/utils/i18n';
import Nullable from '@/types/nullable';
import User from '@/types/user';

type StateProps = {
  contact?: GenericContact;
  isSavingRiskProfile: boolean;
  locale: Locale;
  user?: Nullable<User>;
};

type DispatchProps = {
  saveContactRiskProfile: (profile: Partial<ContactRiskProfile>) => Promise<ContactRiskProfile[]>;
};

type Props = StateProps &
  DispatchProps & {
    data: ContactRiskProfile[];
    onAttach?: (profile: ContactRiskProfile) => void;
    onCreate: (questionnaire?: KycQuestionnaire) => void;
    onDelete?: (profile: ContactRiskProfile) => void;
    onEdit?: (profile: ContactRiskProfile) => void;
  };

const RiskProfilesTable = (props: Props) => {
  const { contact, data, isSavingRiskProfile, locale, user, onAttach, onCreate, onDelete, onEdit, saveContactRiskProfile } = props;

  const tableClassNames: TableClassNames = {
    isBodyHoverActive: true,
  };

  const [riskProfileToUpdate, setRiskProfileToUpdate] = useState<ContactRiskProfile | undefined>(undefined);
  const [showRenameModal, setShowRenameModal] = useState<boolean>(false);

  const onAttachClick = (profile: ContactRiskProfile) => onAttach && onAttach(profile);
  const onDeleteClick = (profile: ContactRiskProfile) => onDelete && onDelete(profile);
  const onEditClick = (profile: ContactRiskProfile) => onEdit && onEdit(profile);

  // EMPTY TABLE DATA
  const getEmptyDataComponent = (): JSX.Element => {
    const i18nEmptyStatePath = 'EmptyStateCard.ContactDetailsRiskProfiles';
    const primaryButton = { label: `${i18nEmptyStatePath}.PrimaryButtonText`, onClick: () => onCreate() };

    return (
      <EmptyStateCard
        i18nVariables={{ name: contact?.fname ?? '' }}
        messagePath={`${i18nEmptyStatePath}.Message`}
        primaryButton={primaryButton}
        titlePath={`${i18nEmptyStatePath}.Title`}
      />
    );
  };

  // CELLS
  const getCreateComparisonCell = (profile: ContactRiskProfile): JSX.Element => {
    if (!onAttach) return <></>;

    return (
      <PrimaryButton className="primary-v2 action-button" onClick={() => onAttachClick(profile)}>
        <FormattedMessage id="RiskProfilesTable.Columns.CreateComparison" />
      </PrimaryButton>
    );
  };

  const getLastModifiedCell = (updatedAt?: string): JSX.Element => {
    if (!updatedAt || updatedAt === '') {
      return <div className="empty-cell" />;
    }

    return <span className="cell">{moment(updatedAt).format(formatMessage('RiskProfilesTable.LastModifiedDateFormat'))}</span>;
  };

  const getMenuActions = () => [
    {
      key: 'edit-profile',
      className: 'edit-profile',
      icon: 'icon-ds-swap-horiz',
      isAllowed: (riskProfile?: ContactRiskProfile) => riskProfile?.availableActions?.edit ?? true,
      onClick: (riskProfile?: ContactRiskProfile) => {
        if (!riskProfile) return;
        onEditClick(riskProfile);
      },
      value: <FormattedMessage id="RiskProfilesTable.ChangeRiskProfile" />,
    },
    {
      key: 'rename-profile',
      icon: 'icon-ds-rename',
      isAllowed: (riskProfile?: ContactRiskProfile) => {
        const hasRiskProfileName = !!riskProfile?.name && !!formatLocalizedString(riskProfile.name);
        const hasProfileTitle = !!riskProfile?.profile.title && !!formatLocalizedString(riskProfile.profile.title);
        return (riskProfile?.availableActions?.edit ?? true) && (hasRiskProfileName || hasProfileTitle);
      },
      onClick: (riskProfile?: ContactRiskProfile) => {
        if (!riskProfile) return;
        setRiskProfileToUpdate(riskProfile);
        setShowRenameModal(true);
      },
      value: <FormattedMessageV2 toLocalize="RiskProfilesTable.Rename" />,
    },
    {
      key: 'delete-profile',
      className: 'destructive',
      icon: 'icon-ds-delete-outline',
      isAllowed: (riskProfile?: ContactRiskProfile) => riskProfile?.availableActions?.delete ?? true,
      onClick: (riskProfile?: ContactRiskProfile) => {
        if (!riskProfile) return;
        onDeleteClick(riskProfile);
      },
      value: <FormattedMessage id="RiskProfilesTable.Delete" />,
    },
  ];

  const getNameCell = ({ name, profile }: ContactRiskProfile): JSX.Element | string => {
    if (!name?.[locale] && !profile.title?.[locale]) {
      return <div className="empty-cell" />;
    }

    if (name?.[locale]) {
      return <span className="cell name-cell">{formatLocalizedString(name)}</span>;
    }

    return (
      <span className="cell name-cell">
        <FormattedMessage id="RiskProfilesTable.NameCell" values={{ name: contact?.fname ?? '', profile: formatLocalizedString(profile.title) || '' }} />
      </span>
    );
  };

  const getRiskProfileCell = (title: LocalizedString): JSX.Element => {
    if (!title) {
      return <>–</>;
    }

    return (
      <Tooltip className="custom-tooltip-v2" message={formatLocalizedString(title)}>
        <span>
          <PillLabel text={formatLocalizedString(title) || ''} />
        </span>
      </Tooltip>
    );
  };

  // HEADER
  const getHeader = (i18nKey: string): string => formatMessage(`RiskProfilesTable.Columns.${i18nKey}`);

  // TABLE COLUMNS
  const getTableColumns: ColumnDef<ContactRiskProfile>[] = useMemo(
    () => [
      {
        accessorFn: (row: ContactRiskProfile) => row[RISK_PROFILES_TABLE_KEYS.NAME],
        header: getHeader('Name'),
        enableSorting: true,
        cell: ({ row }: { row: Row<ContactRiskProfile> }) => getNameCell(row.original),
        sortingFn: customSorting,
      },
      {
        accessorFn: (row: ContactRiskProfile) => formatLocalizedString(row.profile.title),
        header: getHeader('RiskProfile'),
        enableSorting: true,
        cell: ({ row }: { row: Row<ContactRiskProfile> }) => getRiskProfileCell(row.original.profile.title),
      },
      {
        accessorFn: (row: ContactRiskProfile) => row[RISK_PROFILES_TABLE_KEYS.UPDATED_AT],
        header: getHeader('LastModified'),
        cell: ({ row }: { row: Row<ContactRiskProfile> }) => getLastModifiedCell(row.original.updatedAt),
        size: 240,
      },
      {
        enableSorting: false,
        header: '',
        id: 'createComparisonColumn',
        cell: ({ row }: { row: Row<ContactRiskProfile> }) => getCreateComparisonCell(row.original),
        size: 178,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [locale],
  );

  const getExpandedContent = (row: Row<ContactRiskProfile>) => {
    const { _id, profile } = row.original;

    return (
      <div className="risk-profile-info">
        <RiskProfileCard
          key={_id}
          riskProfile={profile}
          locale={locale}
          user={user}
          onDelete={() => onDeleteClick(row.original)}
          onEdit={() => onEditClick(row.original)}
        />
      </div>
    );
  };

  const handleNameRiskProfileSubmit = async (name: string) => {
    if (!riskProfileToUpdate?._id) return;

    const updatedRiskProfile = {
      ...riskProfileToUpdate,
      name: {
        ...riskProfileToUpdate.name,
        [locale]: name,
      },
    };

    await saveContactRiskProfile(updatedRiskProfile);
    setShowRenameModal(false);
    notification.success(
      formatMessage('RiskProfileModal.NameRiskProfile.RenameNotificationBody'),
      formatMessage('RiskProfileModal.NameRiskProfile.RenameNotificationTitle'),
    );
    setRiskProfileToUpdate(undefined);
  };

  const renderRenameModal = () => {
    const loading = {
      isActive: isSavingRiskProfile,
      labelToLocalize: 'Common.Saving',
    };
    return (
      <ModalV2
        body={renderRenameModalBody()}
        loading={loading}
        onClose={() => setShowRenameModal(false)}
        show={showRenameModal}
        title="RiskProfileModal.NameRiskProfile.Title"
      />
    );
  };

  const renderRenameModalBody = () => {
    const formattedName = formatMessage('RiskProfilesTable.NameCell', {
      name: contact?.fname ?? '',
      profile: formatLocalizedString(riskProfileToUpdate?.profile.title) || '',
    });
    const name = riskProfileToUpdate?.name && riskProfileToUpdate.name[locale] ? riskProfileToUpdate.name[locale] : formattedName || undefined;
    return <NameRiskProfile closeModal={() => setShowRenameModal(false)} existingName={name} isRenaming onSubmit={handleNameRiskProfileSubmit} />;
  };

  return (
    <div className="risk-profiles-table">
      <DataTableV2
        columns={getTableColumns}
        data={data}
        enableSorting
        isExpandable
        menuItems={getMenuActions()}
        noDataComponent={getEmptyDataComponent}
        renderExpandedContent={getExpandedContent}
        tableClassNames={tableClassNames}
      />

      {showRenameModal && renderRenameModal()}
    </div>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => {
  const {
    contacts: { current },
    intl: { locale },
    auth: { user },
  } = state;

  return {
    contact: current?.contact,
    locale,
    user,
    isSavingRiskProfile: selectIsLoadingEvery(state, [SET_IS_SAVING_CONTACT_RISK_PROFILE.type]),
  };
};

export default connect(mapStateToProps, {
  saveContactRiskProfile,
})(withErrorBoundary(RiskProfilesTable));
